import React, { Fragment, memo, useCallback, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Tooltip } from '@mui/material'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { CopyableIdRow } from 'src/components/shared/CopyableIdRow'
import { DataRow } from 'src/components/shared/DataRow'
import type { FeeWithdrawal } from 'src/interfaces/feeWithdrawal'
import {
  FEE_WITHDRAWAL_REPORT_NOT_FOUND_MESSAGE,
  buildFeeWithdrawalDetailRows,
} from 'src/utils/paymentsOps/feeWithdrawals'

type ReportAction = 'preview' | 'download'

interface Props {
  withdrawal: FeeWithdrawal | null
  isReportLoading: boolean
  onPreviewReport(withdrawal: FeeWithdrawal): void
  onDownloadReport(withdrawal: FeeWithdrawal): void
  onClose(): void
}

/** Every field of one withdrawal, plus its report actions. The admin's stand-in for a drawer. */
const FeeWithdrawalDetailsModalComponent = ({
  withdrawal,
  isReportLoading,
  onPreviewReport,
  onDownloadReport,
  onClose,
}: Props) => {
  // Which button started the in-flight report fetch, so only that one spins.
  const [lastReportAction, setLastReportAction] = useState<ReportAction | null>(null)

  const detailRows = useMemo(() => {
    return withdrawal ? buildFeeWithdrawalDetailRows(withdrawal) : []
  }, [withdrawal])

  const handlePreview = useCallback(() => {
    if (withdrawal) {
      setLastReportAction('preview')
      onPreviewReport(withdrawal)
    }
  }, [onPreviewReport, withdrawal])

  const handleDownload = useCallback(() => {
    if (withdrawal) {
      setLastReportAction('download')
      onDownloadReport(withdrawal)
    }
  }, [onDownloadReport, withdrawal])

  const hasReport = Boolean(withdrawal?.hasReport)
  const reportTooltip = hasReport ? '' : FEE_WITHDRAWAL_REPORT_NOT_FOUND_MESSAGE

  return (
    <CommonModal
      isOpen={withdrawal !== null}
      title="Fee withdrawal details"
      className={tw`max-w-[560px]`}
      onClose={onClose}
      buttons={
        <Fragment>
          <Button size="small" color="grey" onClick={onClose}>
            Close
          </Button>
          <Tooltip title={reportTooltip}>
            <span>
              <Button
                size="small"
                color="grey"
                disabled={!hasReport || isReportLoading}
                loading={isReportLoading && lastReportAction === 'preview'}
                onClick={handlePreview}
                data-testid="fee-withdrawal-details-preview"
              >
                Preview report
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={reportTooltip}>
            <span>
              <Button
                size="small"
                color="dark"
                disabled={!hasReport || isReportLoading}
                loading={isReportLoading && lastReportAction === 'download'}
                onClick={handleDownload}
                data-testid="fee-withdrawal-details-download"
              >
                Download report
              </Button>
            </span>
          </Tooltip>
        </Fragment>
      }
    >
      {withdrawal ? (
        <div css={tw`flex flex-col`} data-testid="fee-withdrawal-details">
          <CopyableIdRow
            title="Withdrawal ID"
            value={withdrawal.id}
            copiedMessage="Withdrawal ID copied"
            copyAriaLabel="Copy withdrawal ID"
            dataTestId="fee-withdrawal-details-id"
            copyButtonDataTestId="fee-withdrawal-details-copy-id"
          />
          {detailRows.map((row) => {
            return <DataRow key={row.label} title={row.label} text={row.value} />
          })}
        </div>
      ) : null}
    </CommonModal>
  )
}

export const FeeWithdrawalDetailsModal = memo(FeeWithdrawalDetailsModalComponent)
